/**
 * Brand swatch model shared by the color picker and the canvas properties
 * panel. A brand swatch is a brand color the user can BIND to: clicking it in
 * the picker stores the token (`$brand.color.<usage>`) instead of a hex, so a
 * later brand swap re-themes the design. The picker/field also use it to render
 * a token-bound value's resolved swatch color and its chip label.
 */

// Deterministic debug color shown for a token the active brand can't resolve
// (missing field / orphaned binding). Preferred over black so it's obvious.
export const UNRESOLVED_TOKEN_FALLBACK = '#FF00FF';

export interface BrandSwatch {
  /** Merge token this swatch binds to, e.g. `$brand.color.primary`. */
  token: string;
  /** Resolved display color (hex/css) for the swatch preview. */
  hex: string;
  /** Human label for the token chip, e.g. `Primary`. */
  label: string;
}

// Turn a brand color usage key into a chip label:
//   'primary'         -> 'Primary'
//   'buttonSecondary' -> 'Button Secondary'
//   'email_background'-> 'Email Background'
export function brandUsageLabel(usage: string): string {
  return usage
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Parse the usage sub-key out of a `$brand.color.<usage>` token, or null when
// the string isn't a brand color token. Used to label an orphan token (one no
// longer backed by a swatch) without a lookup table.
export function parseBrandColorToken(token: string): string | null {
  const m = /^\$brand\.color\.([a-zA-Z][a-zA-Z0-9_]*)$/.exec(token.trim());
  return m ? m[1] : null;
}

// Minimal brand color row shape (matches the generated BrandColorDto fields we
// need). Kept structural so callers can pass the DTO array directly.
interface BrandColorRow {
  usage?: string | null;
  hexValue?: string | null;
}

/**
 * Build the bindable brand swatch list from a brand's explicit color rows. Only
 * rows with both a usage and a hex become bindable (a usage is what the token
 * resolves against). Deduped by token, input order preserved.
 */
export function buildBrandSwatches(colors: BrandColorRow[] | undefined | null): BrandSwatch[] {
  if (!colors?.length) return [];
  const seen = new Set<string>();
  const out: BrandSwatch[] = [];
  for (const c of colors) {
    if (!c.usage || !c.hexValue) continue;
    const token = `$brand.color.${c.usage}`;
    if (seen.has(token)) continue;
    seen.add(token);
    out.push({ token, hex: c.hexValue, label: brandUsageLabel(c.usage) });
  }
  return out;
}
